import { defineComponent, inject, onMounted, ref } from 'vue';
import SHeaderSection from '@/components/stemdo-components/s-header-section/SHeaderSection.vue';
import StemdoerService from '@/sections/admin/entities/stemdoer/stemdoer.service';
import { Stemdoer } from '@/shared/model/stemdoer.model';
import SBoxStemdoerCompsAndCerts from '@/components/stemdo-components/s-box-stemdoer-comps-and-certs/SBoxStemdoerCompsAndCerts.vue';
import SBoxStemdoerExperience from '@/components/stemdo-components/s-box-stemdoer-experience/SBoxStemdoerExperience.vue';
import SBoxStemdoerEducation from '@/components/stemdo-components/s-box-stemdoer-education/SBoxStemdoerEducation.vue';
import StemdoerOverviewCard from './StemdoerOverviewCard.vue';
import ExperienceModal from './experienceModal/ExperienceModal.vue';
import { useRouter } from '@/plugins/router';
import { IExperience } from '@/shared/model/experience.model';
import { ITechnologyScore } from '@/shared/model/technology-score.model';
import SPopUp from '@/components/stemdo-components/s-pop-up/SPopUp.vue';
import CompsModal from './compsModal/CompsModal.vue';
import { useI18N } from '@/plugins/i18n';
import SShareLink from '@/components/stemdo-components/s-share-link/SShareLink.vue';

export default defineComponent({
  props: {
    stemdoerId: {
      type: String,
      required: true
    }
  },
  components: {
    SHeaderSection,
    SBoxStemdoerCompsAndCerts,
    SBoxStemdoerExperience,
    SBoxStemdoerEducation,
    StemdoerOverviewCard,
    ExperienceModal,
    CompsModal,
    SPopUp,
    SShareLink
  },
  setup(props) {
    const router = useRouter();
    const i18n = useI18N();
    const experienceModalMode = ref<'add' | 'edit'>('add');
    const experienceSelected = ref<IExperience>(undefined);
    const stemdoerService: StemdoerService = inject('stemdoerService');
    const stemdoer = ref<Stemdoer>(Stemdoer.empty());
    const isAddExperienceModalOpen = ref(false);
    const isAddCompsModalOpen = ref(false);
    const compsModalMode = ref<'add' | 'edit'>('add');
    const compsSelected = ref<ITechnologyScore[]>([]);
    const popUpData = ref({
      message: '',
      isOpen: false,
      isValid: true
    });
    const sModalData = ref({
      open: false,
      text: ''
    });

    onMounted(async () => {
      stemdoer.value = await getStemdoerDetail();
    });

    const getStemdoerDetail = async () => {
      try {
        const res = await stemdoerService.find(props.stemdoerId);
        return new Stemdoer(res);
      } catch (err) {
        router.push({ name: 'NotFoundError' });
      }
    };

    const openAddExperienceModal = experience => {
      if (experience) {
        experienceModalMode.value = 'edit';
        experienceSelected.value = experience;
      } else {
        experienceModalMode.value = 'add';
      }
      isAddExperienceModalOpen.value = true;
    };

    const closeAddExperienceModal = () => {
      experienceSelected.value = undefined;
      isAddExperienceModalOpen.value = false;
    };

    const openAddCompsModal = (technologyScores = []) => {
      compsModalMode.value = technologyScores.length > 0 ? 'edit' : 'add';
      compsSelected.value = technologyScores;
      isAddCompsModalOpen.value = true;
    };

    const closeAddCompsModal = () => {
      compsSelected.value = undefined;
      isAddCompsModalOpen.value = false;
    };

    const saveExperience = (experience: IExperience) => {
      if (experienceModalMode.value === 'add') {
        stemdoerService.createExperience(props.stemdoerId, experience).then(async () => {
          stemdoer.value = await getStemdoerDetail();
          closeAddExperienceModal();
          popUpData.value = {
            message: i18n.t('stemdoerDetail.popUp.saveConfirmation').toString(),
            isOpen: true,
            isValid: true
          };
        });
      }
      if (experienceModalMode.value === 'edit') {
        stemdoerService.partialUpdateExperience(props.stemdoerId, experience.id, experience).then(async () => {
          stemdoer.value = await getStemdoerDetail();
          closeAddExperienceModal();
          popUpData.value = {
            message: i18n.t('stemdoerDetail.popUp.editConfirmation').toString(),
            isOpen: true,
            isValid: true
          };
        });
      }
    };

    const deleteExperience = (experience: IExperience) => {
      if (!experience || !experience.id) return;
      stemdoerService
        .deleteExperience(props.stemdoerId, experience.id)
        .then(async () => {
          stemdoer.value = await getStemdoerDetail();
          closeAddExperienceModal();
          popUpData.value = {
            message: i18n.t('stemdoerDetail.popUp.deleteConfirmation').toString(),
            isOpen: true,
            isValid: true
          };
        })
        .catch(error => {
          popUpData.value = {
            message: i18n.t('stemdoerDetail.popUp.deleteError').toString(),
            isOpen: true,
            isValid: false
          };
        });
    };

    const saveComps = async (technologyScores: ITechnologyScore[]) => {
      const serviceMethod =
        compsModalMode.value === 'add' ? stemdoerService.createTechnologyScores : stemdoerService.updateTechnologyScores;

      await serviceMethod(props.stemdoerId, technologyScores);
      stemdoer.value = await getStemdoerDetail();
      closeAddCompsModal();
      popUpData.value = {
        message: i18n
          .t(
            compsModalMode.value === 'add'
              ? 'stemdoerDetail.popUp.saveConfirmationCompetencies'
              : 'stemdoerDetail.popUp.editConfirmationCompetencies'
          )
          .toString(),
        isOpen: true,
        isValid: true
      };
    };

    const openPdfModal = (open: boolean, text: string) => {
      sModalData.value.open = open;
      sModalData.value.text = text;
    };

    return {
      stemdoer,
      isAddExperienceModalOpen,
      experienceModalMode,
      experienceSelected,
      isAddCompsModalOpen,
      compsModalMode,
      compsSelected,
      popUpData,
      saveExperience,
      deleteExperience,
      openPdfModal,
      saveComps,
      openAddExperienceModal,
      closeAddExperienceModal,
      openAddCompsModal,
      closeAddCompsModal
    };
  }
});
